import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { PageListLayout } from '../components/layouts/PageListLayout';
import { TextField } from '../@core/components/mui';
import { investigationService } from '../services/investigationService';
import { useNotificationStore } from '../store/notificationStore';
import { ROUTES } from '../utils/routes';
import type { InvestigationTemplateDto } from '../types/investigation';

/**
 * Investigation templates list page (Module 24).
 */
export const InvestigationTemplatesListPage = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotificationStore();

  const [templates, setTemplates] = useState<InvestigationTemplateDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [activeOnly, setActiveOnly] = useState(true);
  const [search, setSearch] = useState('');

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await investigationService.getInvestigationTemplates(activeOnly);
      setTemplates(result);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to load templates';
      setError(msg);
      showNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOnly]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return templates;
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(term) ||
        (t.description ? t.description.toLowerCase().includes(term) : false)
    );
  }, [templates, search]);

  const handleDelete = async (id: string, isSystemTemplate: boolean) => {
    if (isSystemTemplate) {
      showNotification('System templates cannot be deleted.', 'warning');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      await investigationService.deleteInvestigationTemplate(id);
      showNotification('Template deleted successfully', 'success');
      await loadTemplates();
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to delete template';
      showNotification(msg, 'error');
    }
  };

  const columns: DataTableColumn<InvestigationTemplateDto>[] = [
    {
      id: 'name',
      label: 'Name',
      minWidth: 220,
      render: (t) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {t.name}
          </Typography>
          {t.description && (
            <Typography variant="caption" color="text.secondary">
              {t.description.length > 90 ? `${t.description.slice(0, 90)}…` : t.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 140,
      render: (t) => (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={t.isActive ? 'Active' : 'Inactive'} color={t.isActive ? 'success' : 'default'} size="small" />
          {t.isSystemTemplate && <Chip label="System" size="small" variant="outlined" />}
        </Box>
      ),
    },
    {
      id: 'updatedAt',
      label: 'Updated',
      minWidth: 180,
      render: (t) => <Typography variant="body2">{new Date(t.updatedAt).toLocaleString()}</Typography>,
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 140,
      align: 'right',
      render: (t) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => navigate(`/investigations/templates/${t.id}/edit`)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(t.id, t.isSystemTemplate)}
              disabled={t.isSystemTemplate}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <PageListLayout
      title="Investigation Templates"
      createButton={{ label: 'Create Template', onClick: () => navigate(ROUTES.INVESTIGATION_TEMPLATE_NEW), icon: <AddIcon /> }}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      loading={loading}
      isEmpty={filtered.length === 0}
      emptyState={{
        title: 'No templates',
        description: 'Create an investigation template to get started.',
      }}
      filters={
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Search templates"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flexGrow: 1, minWidth: 240 }}
          />
          <FormControlLabel
            control={<Switch checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />}
            label="Active only"
          />
        </Box>
      }
    >
      <DataTable
        columns={columns}
        rows={filtered}
      />
    </PageListLayout>
  );
};

